import type {
  AddedSpec,
  Episode,
  GeologyBridge,
  RideView,
  WorksClock,
  WorksSnapshot,
} from '@mighty-map/map-engine/bim';
import type { GeoidClip } from '@mighty-map/map-engine/grid';
import { create } from 'zustand';
import type { Pose } from '../../lib/bim/camera';
import { BUDGETS, deviceBudget } from '../../lib/bim/budget';
import { FALLBACK_CLASSES, joinPolicy, type Joined, type ProjectGroup } from '../../lib/bim/classes';
import type { BimSettings } from '../../lib/bim/settings';
import type { TowerFeat } from '../../lib/bim/towers';
import { useViewMode, type ViewMode } from '../../state/viewMode';
import type { Catalog } from './world';
import type { WorldSettings } from '../../lib/bim/settings';

/* Which view BIM was last entered from: a flat 2D map hands over no camera angle, the globe a genuine one. Tracked
 * from module load (the Widget Host loads this with the shell), because the view itself mounts on first entry. */
let enteredFrom: ViewMode = 'map';
useViewMode.subscribe((s, prev) => {
  if (s.mode === 'bim' && prev.mode !== 'bim') enteredFrom = prev.mode;
});
export const bimEnteredFrom = (): ViewMode => enteredFrom;

/**
 * The BIM view's shared state: production's bridge (`window.__nzBim` plus its `nz:bimstate` event), typed and in a
 * store (ADR-0007: tools talk to each other through the store, never window globals).
 *
 * The view owns the model and drives the engine from this state; the Model tab (production's Layers Pro pane) and
 * the BIM tools read it and call the actions. Both sides read the same values, so they can never disagree about
 * what is on.
 */

/** One model layer row: an overlay ON the model (towers, conductors, pillows, clearing, vegetation, a drape), as
 *  distinct from the surface classes the one composite surface is made of. */
export interface BimLayerRow {
  id: string;
  label: string;
  /** The project has this layer at all (a project without conductors shows no conductor row). */
  present: boolean;
  on: boolean;
  count: number | null;
  /** Which vintage is loaded, or a level count: what the row can say about its data. */
  detail: string | null;
  /** Loaded: a tick over an empty layer is a lie, so the box is disabled until then. */
  ready: boolean;
  error: string | null;
  /** Towers only: the splat size. */
  size?: number;
  /** Rows that belong together (Mighty Works' parts) name their group; Layers Pro gathers them under it. Rows
   *  without one are drawn as they always were. */
  group?: string;
  /** An Other Project's row (the release policy's `groups`): Layers Pro lists it under that project. */
  projectGroup?: string;
  /** Within a group, the line a row belongs to (a version 2 works file), with its colour. */
  sub?: string | null;
  swatch?: string | null;
}

/** Mighty Works' verbs while a works file is loaded (production's bridge `demo*` verbs). The clock runs in the model;
 *  a player reads `snapshot()` as often as it wants to show it moving. */
export interface WorksApi {
  label: string;
  /** Mighty Works' speeds, minutes a second, with its labels: the build's, and In service's (slower). */
  speeds: [number, string][];
  serviceSpeeds: [number, string][];
  snapshot(): WorksSnapshot | null;
  setClock(p: WorksClock): void;
  /** Fly to a TBM, the next train in service, onto a station's platform, to the opening ceremony, or ride a train
   *  (again for the next seat); all can be under the ground, so these switch underground mode on where the project
   *  has it. `index` picks a TBM or a station. */
  zoom(kind: 'tbm' | 'train' | 'station' | 'ceremony' | 'ride' | 'launch', index?: number): boolean;
  /** The two episodes: 'build' (the first launch to Opening day) and 'use' (In service: Opening day from the ribbon
   *  cutting, the camera at the ceremony). */
  episode(ep: Episode): boolean;
  /** Choose the train to follow and ride (an id from the snapshot's train list), or null to let go. */
  train(id: string | null): boolean;
  /** Riding a train: its view, or null. */
  riding(): RideView | null;
  follow(on: boolean): boolean;
  following(): boolean;
}

/** The project grid the model takes Data Passport's items on (MGA2020, `zone`), and its AUSGeoid2020 clip (null when
 *  it could not be read: an ellipsoidal height then has no AHD and its item goes on the ground). */
export interface BimGrid {
  wkid: number;
  zone: number;
  geoid: GeoidClip | null;
}

/** One item Data Passport added to the model: its row under Added data in the Model tab. */
export interface AddedRow {
  id: string;
  title: string;
  /** A file that made several items: its name, their sub-group. */
  group: string | null;
  count: number;
  /** Features the cap left out ("first N"). */
  dropped: number;
  mode: 'draped' | '3d';
  on: boolean;
  opacity: number;
  /** What it draws and at what size, for its fine controls (point size, line width). */
  points: boolean;
  lines: boolean;
  size: number;
  width: number;
}

/** Data Passport's verbs on the model (the design contract's overlay API; the Esri edition's `__nzBim.added`). */
export interface AddedApi {
  /** Add an item on the grid (replacing one with its id): what it kept. */
  add(spec: AddedSpec): { count: number; dropped: number };
  remove(id: string): void;
  setVisible(id: string, on: boolean): void;
  setOpacity(id: string, v: number): void;
  setPointSize(id: string, px: number): void;
  setLineWidth(id: string, px: number): void;
  zoomTo(id: string): boolean;
  flash(id: string): void;
}

/** Whether the model view can serve the tools: still starting (or never opened), up, or not here at all (no model on
 *  this server, none set up for the project, or it failed to load). */
export type BimAvailability = 'starting' | 'ready' | 'unavailable';

/** The verbs other tools use on the model (production's bridge verbs). Null until the viewer is up. */
export interface BimApi {
  canvas: HTMLCanvasElement;
  /** The tileset index has arrived ("loading" versus "loaded and empty"). */
  loaded(): boolean;
  getPose(): Pose | null;
  flyTo(lat: number, lon: number, dist?: number, heading?: number, pitch?: number): boolean;
  /** Called whenever the camera moves: every frame of a drag or a flight. Returns the undo. */
  onCamera(handler: () => void): () => void;
  /** The project's home pose (where a session's first open lands), or null when it names none. */
  home(): Pose | null;
  /** What is under a screen point: class, height (AHD) and position. */
  identify(clientX: number, clientY: number): { lat: number; lon: number; height: number; row: number } | null;
  /** Height of the model surface, or null where no tile has arrived. */
  sampleHeight(lat: number, lon: number): number | null;
  /** Height and source class at a point: what a BIM section is made of. */
  sampleAt(lat: number, lon: number): { height: number; row: number } | null;
  /** Geodetic -> client pixels, so a label can follow the camera. */
  project(lat: number, lon: number, h?: number): { x: number; y: number; behind: boolean } | null;
  /** Draw on the model, by id; null erases. */
  setOverlay(
    id: string,
    spec: {
      points: [number, number, number][];
      kind?: 'line' | 'loop' | 'points';
      colour?: number;
      offset?: number;
      size?: number;
      width?: number;
    } | null,
  ): void;
  clearOverlays(prefix?: string): void;
  /** Stop the camera responding to the pointer, so a tool can own drags outright. */
  setNavEnabled(on: boolean): void;
  /** The vertical field of view in degrees. */
  fov(): number;
  /** Set the vertical field of view in degrees. */
  setFov(deg: number): void;
  /** Control the roadster on the model surface. */
  roadster: {
    set(on: boolean): boolean;
    on(): boolean;
    drive(
      throttle: number,
      steer: number,
      dt: number,
      orbit?: number,
      brake?: boolean,
      lift?: number,
      boost?: boolean,
    ): { speed: number; flight: string; mode: string; under: boolean; doors: boolean } | null;
    /** 'drive', 'fly', 'sub' or 'phase'. Pressing the one that is on goes back to Drive. */
    setMode(mode: 'drive' | 'fly' | 'sub' | 'phase'): string | null;
    toggleDoors(): boolean;
    /** The vehicles on offer. */
    vehicles: Array<{ id: string; label: string; modes: string[] }>;
    /** The one chosen. */
    vehicle(): string;
    /** Choosing while driving swaps it where it stands. */
    setVehicle(id: string, logos?: boolean): string;
    flight(): string | null;
  };
  fly(fwd: number, right: number, up: number): void;
  look(dHeading: number, dPitch: number, pivot?: number): void;
  dolly(metres: number): void;
  advance(metres: number): void;
  /** Put the view back on the data, wherever it drifted to. */
  recover(): boolean;
  /** PNG data URL of the live view, for Snapshot and Print. */
  snapshot(): string | null;
  /** Whether Flash can blink a model-layer row (one whose drawing is meshes of its own). */
  canFlash(id: string): boolean;
  /** Blink what these model-layer rows draw, in a colour (Layers Pro's Flash): how many meshes blinked, 0 for none. */
  flash(ids: readonly string[], colour: string): Promise<number>;
  /** Flash near's place for these rows: the nearest drawn part to the view's centre, and a distance that frames it. */
  nearestFlash(ids: readonly string[]): { lat: number; lon: number; dist: number } | null;
  /** The structure schedule, for Search. */
  towers(): TowerFeat[];
  /** Select a structure by name: towers on, fly to it, raise its card. False if unknown. */
  focus(name: string): boolean;
  /** Data Passport's items: null where the project names no grid (Data Passport is off in BIM there). */
  added: AddedApi | null;
}

interface BimState {
  /** The bake's classes joined against the release policy: the only list any consumer sees. */
  join: Joined;
  /** Class visibility (true = switched off) and opacity (absent = solid). */
  hidden: Record<number, boolean>;
  opacity: Record<number, number>;
  /** The soloed class, and whether any visibility has been touched: the Model tab's Reset shows on either. */
  solo: number | null;
  touched: boolean;
  wire: boolean;
  /** Vertical exaggeration; 0 is flat. */
  ve: number;
  /** The model layer rows, published by the view. */
  layers: BimLayerRow[];
  towersOn: boolean;
  towerSize: number;
  /** Conductor sets by id; a set with no entry is ON. */
  condOn: Record<string, boolean>;
  pillowsOn: boolean;
  clearingOn: boolean;
  vegOn: boolean;
  /** Raster drapes by id (initialised once from the manifest's `visible`). */
  drapeOn: Record<string, boolean>;
  /** The relayed NSW imagery drape. */
  imageryOn: boolean;
  api: BimApi | null;
  /** The grid Data Passport's items go in on, once the view has the project's settings (null: none named). */
  grid: BimGrid | null;
  /** Data Passport's items on the model, in the order added. */
  added: AddedRow[];
  /** Published by the view: what a tool should say while `api` is null. */
  availability: BimAvailability;
  /** The tool that owns clicks on the model (Estimator, Measure, Terrain): identify stays quiet while one does. */
  claim: string | null;
  /** Underground mode (Mighty Twin's underground widget): whether the project has it, whether it is on (it starts off
   *  on every load: a way of looking, not a property of the model), and how solid the glass ground is. */
  canUnderground: boolean;
  underground: boolean;
  groundOpacity: number;
  /** The ground stack's two sliders (WORLD_MODEL decision 13), in metres: the apron each layer's pads ease onto the
   *  ground across, and the band a project's ground eases onto Mighty World's across (no pack has a project ground
   *  yet); and how many pad tiles the drawn layers brought (none: no sliders). */
  padApron: number;
  padBand: number;
  padTiles: number;
  /** Mighty Works: its verbs while loaded, its label, why it failed, and its parts as Layers Pro switches them (a part
   *  with no entry is on). */
  works: WorksApi | null;
  worksLabel: string;
  worksError: string;
  /** The geology (a structures entry drawn as 'geology'): whether the model has one (the tray's Geology tile), its row
   *  name; whether its Widget Host ribbon or its side pane is open (the first opening loads it; a click probes it while
   *  one is; it stays drawn as set when both close);
   *  its bridge once loaded, why it failed, and a counter the panels re-read on (the bridge's state changed). */
  geologyAvailable: boolean;
  geologyLabel: string;
  geologyRibbonOn: boolean;
  geologyPaneOn: boolean;
  geology: GeologyBridge | null;
  geologyError: string;
  geologyRev: number;
  worksParts: Record<string, boolean>;
  /** Live transit's rows as Layers Pro switches them (a row with no entry is on). */
  transitParts: Record<string, boolean>;
  /** The bridges and tunnels rows, the same way. */
  infraParts: Record<string, boolean>;
  /** The water surface. */
  waterOn: boolean;
  /** 0..100 translucency for the water surface (0 = as styled). */
  waterTranslucency: number;
  /** The buildings. */
  buildingsOn: boolean;
  /** The landmarks' models (a pack's `landmarks` list). */
  landmarksOn: boolean;
  landmarkBuildingsOn: boolean;
  stationsOn: boolean;
  wharvesOn: boolean;
  /** The motorways lane by lane, and the railway track by track. */
  lanesOn: boolean;
  railOn: boolean;
  /** Other Projects (the release policy's `groups`), and the ones with something in this model; their towers' rows
   *  by group id, OFF by default like Towers itself. */
  projectGroups: ProjectGroup[];
  otherProjects: { id: string; title: string }[];
  groupTowersOn: Record<string, boolean>;
  /** Mighty World group on the Model tab (while the pack names `world` and the catalogue loaded). */
  worldActive: boolean;
  worldReleaseId: string;
  worldCredits: string[];
  worldCatalog: Catalog | null;
  worldPack: BimSettings | null;
  worldConfig: WorldSettings | null;
  worldUsesWorld: Record<string, boolean>;
  worldDistrictOn: Record<string, boolean>;
  worldGroupOn: boolean;
  worldChannelOn: Record<string, boolean>;
  worldHasTransitRows: boolean;
  worldHasPeople: boolean;
  /** Structure counts per Transport › Bridges / Tunnels row. */
  worldInfraRowCounts: Partial<Record<string, number>> | null;
  worldBridgeModelRowCounts: Partial<Record<string, number>> | null;

  setJoin: (join: Joined) => void;
  setClassVisible: (row: number, on: boolean) => void;
  setClassOpacity: (row: number, v: number) => void;
  isolateClass: (row: number | null) => void;
  resetSurfaces: () => void;
  setWireframe: (on: boolean) => void;
  setExaggeration: (k: number) => void;
  setLayerVisible: (id: string, on: boolean) => void;
  setLayerSize: (id: string, v: number) => void;
  setClaim: (tool: string | null) => void;
  setUnderground: (on: boolean) => void;
  setGroundOpacity: (v: number) => void;
  setPadApron: (m: number) => void;
  setPadBand: (m: number) => void;
}

export const useBim = create<BimState>()((set, get) => ({
  join: joinPolicy(FALLBACK_CLASSES, null, 'fallback'),
  hidden: {},
  opacity: {},
  solo: null,
  touched: false,
  wire: false,
  ve: 1,
  layers: [],
  // OFF by default, as in production: the tower splats are unfinished work, present but dark.
  towersOn: false,
  towerSize: 2,
  condOn: {},
  pillowsOn: true,
  // The clearing model starts OFF: a ground-covering surface on by default would change what the model looks like
  // for everyone, which is not a decision this layer gets to make for them.
  clearingOn: false,
  // Off on a phone (lib/bim/budget): vegetation is the heaviest layer, about 1.2 GB of a 2.8 GB page.
  vegOn: BUDGETS[deviceBudget()].vegOn,
  drapeOn: {},
  imageryOn: false,
  api: null,
  grid: null,
  added: [],
  availability: 'starting',
  claim: null,
  canUnderground: false,
  underground: false,
  groundOpacity: 0.5,
  // The engine's own defaults (pads.ts APRON_DEFAULT, BAND_DEFAULT): imported, they would load three.js with the map.
  padApron: 12,
  padBand: 50,
  padTiles: 0,
  works: null,
  worksLabel: '',
  worksError: '',
  geologyAvailable: false,
  geologyLabel: '',
  geologyRibbonOn: false,
  geologyPaneOn: false,
  geology: null,
  geologyError: '',
  geologyRev: 0,
  worksParts: {},
  transitParts: {},
  infraParts: {},
  waterOn: true,
  waterTranslucency: 0,
  buildingsOn: true,
  landmarksOn: true,
  landmarkBuildingsOn: true,
  stationsOn: true,
  wharvesOn: true,
  lanesOn: true,
  railOn: true,
  projectGroups: [],
  otherProjects: [],
  groupTowersOn: {},
  worldActive: false,
  worldReleaseId: '',
  worldCredits: [],
  worldCatalog: null,
  worldPack: null,
  worldConfig: null,
  worldUsesWorld: {},
  worldDistrictOn: {},
  worldGroupOn: true,
  worldChannelOn: {},
  worldHasTransitRows: false,
  worldHasPeople: false,
  worldInfraRowCounts: null,
  worldBridgeModelRowCounts: null,

  setJoin: (join) =>
    set({
      join: {
        ...join,
        classes: Array.isArray(join.classes) ? join.classes : [],
      },
    }),
  setClassVisible: (row, on) => {
    // A row the release policy withheld is not addressable: without this guard any caller could walk a withheld
    // surface back on with one line. Touching a class by hand ends the solo.
    if (!get().join.classes.some((c) => c.row === row)) return;
    set((s) => ({ hidden: { ...s.hidden, [row]: !on }, solo: null, touched: true }));
  },
  setClassOpacity: (row, v) => {
    const k = Math.min(1, Math.max(0, v));
    set((s) => ({ opacity: { ...s.opacity, [row]: k } }));
  },
  isolateClass: (row) => {
    // Seeded with the withheld rows, so that "show everything again" does not report them as visible.
    const { join } = get();
    const next: Record<number, boolean> = {};
    for (const w of join.withheld) next[w] = true;
    for (const c of join.classes) next[c.row] = !(row == null || c.row === row);
    set({ hidden: next, solo: row, touched: true });
  },
  resetSurfaces: () => {
    const { join } = get();
    const next: Record<number, boolean> = {};
    for (const w of join.withheld) next[w] = true;
    set({ hidden: next, opacity: {}, solo: null, touched: false });
  },
  setWireframe: (on) => set({ wire: on }),
  setExaggeration: (k) => set({ ve: k }),
  setLayerVisible: (id, on) => {
    if (id === 'imagery:all') {
      // Basemap › Imagery: the live imagery and every baked drape together.
      set((s) => ({ imageryOn: on, drapeOn: Object.fromEntries(Object.keys(s.drapeOn).map((k) => [k, on])) }));
      return;
    }
    if (id.startsWith('drape:')) {
      const rid = id.slice('drape:'.length);
      set((s) => ({ drapeOn: { ...s.drapeOn, [rid]: on } }));
    } else if (id.startsWith('works:')) {
      const part = id.slice('works:'.length);
      set((s) => ({ worksParts: { ...s.worksParts, [part]: on } }));
    } else if (id.startsWith('transit:')) {
      const part = id.slice('transit:'.length);
      set((s) => ({ transitParts: { ...s.transitParts, [part]: on } }));
    } else if (id.startsWith('infra:')) {
      const part = id.slice('infra:'.length);
      set((s) => ({ infraParts: { ...s.infraParts, [part]: on } }));
    } else if (id === 'ground') {
      // The Mighty World TIN as one switch: every surface it is made of. A row the release policy withheld is not in
      // join.classes, so it stays withheld.
      const { join } = get();
      set((s) => {
        const next = { ...s.hidden };
        for (const c of join.classes) next[c.row] = !on;
        return { hidden: next, solo: null };
      });
    } else if (id === 'water') set({ waterOn: on });
    else if (id === 'buildings') set({ buildingsOn: on });
    else if (id === 'landmarks') set({ landmarksOn: on });
    else if (id === 'landmark-buildings') set({ landmarkBuildingsOn: on });
    else if (id === 'stations') set({ stationsOn: on });
    else if (id === 'wharves') set({ wharvesOn: on });
    else if (id === 'lanes') set({ lanesOn: on });
    else if (id === 'rail') set({ railOn: on });
    else if (id === 'imagery') set({ imageryOn: on });
    else if (id === 'towers') set({ towersOn: on });
    else if (id.startsWith('towers:')) {
      const g = id.slice('towers:'.length);
      set((s) => ({ groupTowersOn: { ...s.groupTowersOn, [g]: on } }));
    } else if (id === 'pillows') set({ pillowsOn: on });
    else if (id === 'clearing') set({ clearingOn: on });
    else if (id === 'vegetation') set({ vegOn: on });
    else set((s) => ({ condOn: { ...s.condOn, [id]: on } }));
  },
  // Splats have no fixed footprint on screen, so size is a real control: too small and the structures dissolve.
  setLayerSize: (id, v) => {
    if (id === 'towers') set({ towerSize: Math.min(5, Math.max(0.6, Number(v) || 2)) });
  },
  setClaim: (tool) => set({ claim: tool }),
  // Only where the project has it: elsewhere the switch does nothing and the engine is never told.
  setUnderground: (on) => {
    if (get().canUnderground) set({ underground: !!on });
  },
  setGroundOpacity: (v) => {
    if (get().canUnderground && Number.isFinite(v)) set({ groundOpacity: Math.min(1, Math.max(0, v)) });
  },
  setPadApron: (m) => {
    if (Number.isFinite(m)) set({ padApron: Math.min(50, Math.max(0, m)) });
  },
  setPadBand: (m) => {
    if (Number.isFinite(m)) set({ padBand: Math.min(200, Math.max(0, m)) });
  },
}));
